import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { compactActionSx } from "./ownerCenterUi";
import { dateInputValue, legacyFirstDueDateInput, OWNER_RECURRING_FREQUENCIES } from "./ownerRecurringUi";
import { OwnerVoiceFormControls, useOwnerVoiceForm, useOwnerVoiceOperator, voiceFieldProps } from "../voice/OwnerVoiceOperatorContext";

function todayDate() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function emptyForm() {
  const today = todayDate();
  return {
    name: "",
    direction: "expense",
    amountMode: "fixed",
    fixedAmount: "",
    estimatedAmount: "",
    currency: "ARS",
    frequency: "monthly",
    dueDay: String(new Date().getDate()),
    defaultAccountId: "",
    area: "Personal",
    category: "",
    startDate: today,
    firstDueDate: today,
    endDate: "",
    notes: "",
    clientId: "",
    serviceId: "",
    initiativeId: "",
    projectTermId: "",
    quickServiceName: "",
  };
}

function formFromPlan(plan) {
  if (!plan) return emptyForm();
  return {
    name: plan.name || "",
    direction: plan.direction || "expense",
    amountMode: plan.amountMode || "fixed",
    fixedAmount: plan.fixedAmount == null ? "" : String(plan.fixedAmount),
    estimatedAmount: plan.estimatedAmount == null ? "" : String(plan.estimatedAmount),
    currency: plan.currency || "ARS",
    frequency: plan.frequency || "monthly",
    dueDay: String(plan.dueDay || (plan.firstDueDate ? new Date(plan.firstDueDate).getUTCDate() : new Date().getDate())),
    defaultAccountId: idOf(plan.defaultAccountId),
    area: plan.area || "Personal",
    category: plan.category || "",
    startDate: dateInputValue(plan.startDate) || todayDate(),
    firstDueDate: legacyFirstDueDateInput(plan) || dateInputValue(plan.startDate) || todayDate(),
    endDate: dateInputValue(plan.endDate),
    notes: plan.notes || "",
    clientId: idOf(plan.clientId),
    serviceId: idOf(plan.serviceId),
    initiativeId: idOf(plan.initiativeId),
    projectTermId: idOf(plan.projectTermId),
    quickServiceName: "",
  };
}

export default function OwnerRecurringFinanceDialog({ open, onClose, accounts = [], onSaved, preferredClient = null, plan = null, initialValues = null }) {
  const voice = useOwnerVoiceOperator();
  const isEditing = Boolean(idOf(plan));
  const hasHistory = Boolean(plan?.hasHistory || Number(plan?.history?.sampleCount || 0) > 0);
  const structuralLocked = isEditing && hasHistory;
  const projectFinancingPlan = String(plan?.projectTermId?.billingMode || "") === "installments";
  const [form, setForm] = React.useState(emptyForm);
  const [clients, setClients] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [initiatives, setInitiatives] = React.useState([]);
  const [projectTerms, setProjectTerms] = React.useState([]);
  const [loadingProjectTerms, setLoadingProjectTerms] = React.useState(false);
  const [loadingRelations, setLoadingRelations] = React.useState(false);
  const [relationsError, setRelationsError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const preferredId = idOf(preferredClient);
    const initial = isEditing ? formFromPlan(plan) : {
      ...emptyForm(),
      ...(preferredId ? { direction: "income", clientId: preferredId, area: "Freelancer", category: "Servicio recurrente" } : {}),
      ...(initialValues && typeof initialValues === "object" ? initialValues : {}),
    };
    setForm(initial);
    setError("");
    setRelationsError("");
    setLoadingRelations(true);
    Promise.all([
      ownerApi.listClients({ limit: 250 }),
      ownerApi.listServices({ includeInactive: false }),
      ownerApi.listInitiatives({ status: "", limit: 100 }),
    ]).then(([clientResponse, serviceResponse, initiativeResponse]) => {
      if (cancelled) return;
      setClients(clientResponse?.clients || []);
      setServices((serviceResponse?.services || []).filter((row) => row.active !== false));
      setInitiatives((initiativeResponse?.initiatives || []).filter((row) => row.status !== "archived"));
    }).catch(() => {
      if (!cancelled) setRelationsError("No pude cargar clientes y servicios. Los gastos recurrentes siguen editables; para un cobro comercial necesitás esas relaciones.");
    }).finally(() => {
      if (!cancelled) setLoadingRelations(false);
    });
    return () => { cancelled = true; };
  }, [open, preferredClient, isEditing, plan, initialValues]);

  React.useEffect(() => {
    if (!open || !form.initiativeId) { setProjectTerms([]); return undefined; }
    let cancelled = false;
    setLoadingProjectTerms(true);
    ownerApi.projectAgreement(form.initiativeId)
      .then((response) => {
        if (cancelled) return;
        setProjectTerms((response?.agreement?.terms || []).filter((row) => row.status !== "cancelled"));
      })
      .catch(() => { if (!cancelled) setProjectTerms([]); })
      .finally(() => { if (!cancelled) setLoadingProjectTerms(false); });
    return () => { cancelled = true; };
  }, [open, form.initiativeId]);

  const eligibleAccounts = accounts.filter((account) => !account.archived && account.currency === form.currency);
  const selectedService = services.find((row) => idOf(row) === String(form.serviceId));
  const selectedClient = clients.find((row) => idOf(row) === String(form.clientId)) || preferredClient || plan?.clientId;
  const selectedInitiative = initiatives.find((row) => idOf(row) === String(form.initiativeId)) || plan?.initiativeId || null;
  const projectRelationLocked = hasHistory && Boolean(idOf(plan?.initiativeId));
  const termRelationLocked = hasHistory && Boolean(idOf(plan?.projectTermId));

  const changeInitiative = (initiativeId) => {
    const initiative = initiatives.find((row) => idOf(row) === String(initiativeId));
    const relatedClientId = idOf(initiative?.client || initiative?.clientId);
    setForm((current) => ({
      ...current,
      initiativeId,
      projectTermId: "",
      ...(current.direction === "income" && relatedClientId && !current.clientId ? { clientId: relatedClientId } : {}),
    }));
  };

  const changeService = (serviceId) => {
    if (serviceId === "__quick__") {
      setForm((current) => ({ ...current, serviceId, quickServiceName: "" }));
      return;
    }
    const service = services.find((row) => idOf(row) === String(serviceId));
    setForm((current) => ({
      ...current,
      serviceId,
      quickServiceName: "",
      name: service ? `${service.name}${selectedClient?.name ? ` · ${selectedClient.name}` : ""}` : current.name,
      currency: service?.currency || current.currency,
      fixedAmount: service?.basePrice ? String(service.basePrice) : current.fixedAmount,
      amountMode: "fixed",
      area: "Freelancer",
      category: service?.category || "Servicio recurrente",
      defaultAccountId: "",
    }));
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      let serviceId = form.direction === "income" ? form.serviceId : "";
      let service = selectedService;
      if (!structuralLocked && form.direction === "income" && serviceId === "__quick__") {
        if (!form.quickServiceName.trim()) throw new Error("Decime cómo se llama el servicio.");
        const baseAmount = form.amountMode === "fixed" ? Number(form.fixedAmount || 0) : Number(form.estimatedAmount || 0);
        const created = await ownerApi.createService({
          name: form.quickServiceName.trim(),
          description: "Creado desde un acuerdo recurrente Owner.",
          category: form.category || "Freelancer",
          billingMode: form.frequency === "monthly" ? "monthly" : "custom",
          currency: form.currency,
          basePrice: Number.isFinite(baseAmount) ? baseAmount : 0,
          estimatedCost: 0,
          active: true,
        });
        service = created?.service || null;
        serviceId = idOf(service);
      }

      if (!structuralLocked && form.direction === "income" && !form.clientId) throw new Error("Elegí el cliente del acuerdo recurrente.");
      if (!structuralLocked && form.direction === "income" && !serviceId) throw new Error("Elegí el servicio del acuerdo recurrente.");
      if (!form.startDate) throw new Error("Indicá desde cuándo existe el acuerdo.");
      if (!form.firstDueDate) throw new Error("Indicá el primer vencimiento.");

      const name = form.name.trim()
        || (service?.name ? `${service.name}${selectedClient?.name ? ` · ${selectedClient.name}` : ""}` : "")
        || (form.direction === "income" ? "Cobro recurrente" : "Pago recurrente");

      const payload = {
        name,
        amountMode: form.amountMode,
        fixedAmount: form.amountMode === "fixed" ? Number(form.fixedAmount) : null,
        estimatedAmount: form.amountMode === "variable" && form.estimatedAmount ? Number(form.estimatedAmount) : null,
        defaultAccountId: form.defaultAccountId || null,
        endDate: form.endDate || null,
        area: form.area,
        category: form.category,
        notes: form.notes,
        initiativeId: form.initiativeId || null,
        projectTermId: form.projectTermId || null,
        ...(form.frequency === "monthly" && !projectFinancingPlan ? { dueDay: Number(form.dueDay || 1) } : {}),
      };

      if (!structuralLocked) {
        Object.assign(payload, {
          direction: form.direction,
          currency: form.currency,
          frequency: form.frequency,
          startDate: form.startDate,
          firstDueDate: form.firstDueDate,
          clientId: form.direction === "income" ? (form.clientId || null) : null,
          serviceId: form.direction === "income" ? (serviceId || null) : null,
        });
      }

      const response = isEditing
        ? await ownerApi.updateRecurringFinance(idOf(plan), payload)
        : await ownerApi.createRecurringFinance(payload);
      await onSaved?.(response?.plan || null);
      onClose?.();
      return response?.plan || true;
    } catch (err) {
      setError(formatApiError(err, err?.message || `No pude ${isEditing ? "actualizar" : "crear"} el acuerdo recurrente.`));
      return false;
    } finally {
      setSaving(false);
    }
  };

  useOwnerVoiceForm({
    id: "recurring",
    open,
    config: {
      title: isEditing ? "Editar acuerdo recurrente" : (form.direction === "income" ? "Cobro recurrente" : "Pago recurrente"),
      values: form,
      setValue: (name, rawValue) => {
        let value = rawValue;
        if (["startDate", "firstDueDate", "endDate"].includes(name) && value) value = String(value).slice(0, 10);
        if (name === "initiativeId") return changeInitiative(value);
        if (name === "serviceId") {
          const raw = String(value || "");
          if (raw.startsWith("__quick__:")) {
            const quickName = raw.slice("__quick__:".length).trim();
            setForm((current) => ({
              ...current,
              serviceId: "__quick__",
              quickServiceName: quickName,
              name: quickName ? `${quickName}${selectedClient?.name ? ` · ${selectedClient.name}` : ""}` : current.name,
              area: "Freelancer",
              category: "Servicio recurrente",
            }));
            return;
          }
          return changeService(value);
        }
        setForm((current) => ({
          ...current,
          [name]: value,
          ...(name === "direction" && value === "income" ? { area: "Freelancer", category: "Servicio recurrente" } : {}),
          ...(name === "currency" ? { defaultAccountId: "" } : {}),
        }));
      },
      onSubmit: submit,
      onCancel: onClose,
      successMessage: isEditing ? "Acuerdo actualizado." : "Acuerdo recurrente guardado.",
      afterSubmit: (_result, previousContext) => selectedClient ? {
        context: previousContext || { type: "person", entity: selectedClient },
        prompt: `Acuerdo recurrente guardado para ${selectedClient.name}. ¿Seguimiento, propuesta, proyecto, nota u otra acción?`,
      } : null,
      fields: [
        { name: "direction", label: "tipo", prompt: "¿Es un pago recurrente o un cobro recurrente?", kind: "choice", required: true, when: () => !structuralLocked, options: [
          { value: "expense", label: "Pago", aliases: ["gasto", "pago recurrente"] },
          { value: "income", label: "Cobro", aliases: ["ingreso", "servicio recurrente", "cobro recurrente"] },
        ] },
        { name: "clientId", label: "cliente", prompt: "¿A qué persona o cliente corresponde?", kind: "choice", required: true, speakOptions: false, when: (values) => !structuralLocked && values.direction === "income", skipIfFilled: true, options: clients.map((row) => ({ value: idOf(row), label: row.name, aliases: [row.name, row.company].filter(Boolean) })) },
        { name: "serviceId", label: "servicio", prompt: "¿Qué servicio representa? Si no existe, decime su nombre y lo creo.", kind: "choice", required: true, speakOptions: false, when: (values) => !structuralLocked && values.direction === "income", parse: (raw) => {
          const normalized = String(raw || "").trim().toLowerCase();
          const existing = services.find((row) => {
            const serviceName = String(row.name || "").trim().toLowerCase();
            return serviceName && (normalized === serviceName || normalized.includes(serviceName));
          });
          if (existing) return idOf(existing);
          const cleaned = String(raw || "").replace(/^(crear|nuevo|nueva)\s+servicio\s*/i, "").replace(/^servicio\s+/i, "").trim();
          return cleaned ? `__quick__:${cleaned}` : "__quick__";
        }, options: [{ value: "__quick__", label: "Crear servicio", aliases: ["crear servicio", "nuevo servicio"] }, ...services.map((row) => ({ value: idOf(row), label: row.name, aliases: [row.name] }))] },
        { name: "quickServiceName", label: "nombre del servicio", prompt: "¿Cómo se llama el servicio?", kind: "text", required: (values) => values.serviceId === "__quick__", skipIfFilled: true, when: (values) => !structuralLocked && values.direction === "income" && values.serviceId === "__quick__" },
        { name: "initiativeId", label: "proyecto", prompt: "¿Querés vincular esta mensualidad a un proyecto? Decí siguiente si no.", kind: "choice", speakOptions: false, options: [{ value: "", label: "Sin proyecto", aliases: ["ninguno", "sin proyecto", "siguiente"] }, ...initiatives.map((row) => ({ value: idOf(row), label: row.name, aliases: [row.name, row.client?.name].filter(Boolean) }))] },
        { name: "projectTermId", label: "acuerdo", prompt: "¿A qué acuerdo o mensualidad del proyecto corresponde? Decí siguiente si no.", kind: "choice", speakOptions: false, when: (values) => Boolean(values.initiativeId), options: [{ value: "", label: "Sólo proyecto", aliases: ["ninguno", "contrato general", "siguiente"] }, ...projectTerms.map((row) => ({ value: idOf(row), label: row.title, aliases: [row.title] }))] },
        { name: "name", label: "nombre", aliases: ["nombre"], prompt: "¿Cómo querés reconocer este acuerdo?", kind: "text", skipIfFilled: true },
        { name: "amountMode", label: "monto", prompt: "¿El monto es fijo o variable?", kind: "choice", required: true, options: [{ value: "fixed", label: "Fijo" }, { value: "variable", label: "Variable" }] },
        { name: "currency", label: "moneda", prompt: "¿Pesos argentinos o dólares?", kind: "choice", required: true, when: () => !structuralLocked, options: [{ value: "ARS", label: "Pesos", aliases: ["ars", "pesos"] }, { value: "USD", label: "Dólares", aliases: ["usd", "dolares"] }] },
        { name: "fixedAmount", label: "importe por ocurrencia", aliases: ["monto", "precio"], prompt: "¿Cuánto se espera por vez?", kind: "number", required: (values) => values.amountMode === "fixed", when: (values) => values.amountMode === "fixed" },
        { name: "estimatedAmount", label: "importe estimado", aliases: ["monto estimado"], prompt: "¿Qué monto estimado uso hasta tener historial?", kind: "number", when: (values) => values.amountMode === "variable" },
        { name: "frequency", label: "frecuencia", prompt: "¿Cada cuánto ocurre: semanal, mensual, trimestral o anual?", kind: "choice", required: true, when: () => !structuralLocked, options: OWNER_RECURRING_FREQUENCIES },
        { name: "dueDay", label: "día de cobro", aliases: ["dia de cobro", "día de pago", "dia de pago"], prompt: "¿Qué día del mes querés cobrar?", kind: "number", required: true, when: (values) => values.frequency === "monthly" && !projectFinancingPlan },
        { name: "startDate", label: "inicio", prompt: "¿Desde cuándo existe el acuerdo?", kind: "datetime", required: true, when: () => !structuralLocked },
        { name: "firstDueDate", label: "primer vencimiento", prompt: "¿Cuál es el primer vencimiento?", kind: "datetime", required: true, when: () => !structuralLocked },
        { name: "endDate", label: "fin", prompt: "¿Tiene fecha de finalización? Decí siguiente si no.", kind: "datetime" },
        { name: "defaultAccountId", label: "cuenta", prompt: "¿En qué cuenta suele impactar? Decí siguiente para elegir al registrar.", kind: "choice", speakOptions: false, options: [{ value: "", label: "Elegir al registrar", aliases: ["despues", "ninguna"] }, ...eligibleAccounts.map((account) => ({ value: idOf(account), label: account.name, aliases: [account.name] }))] },
        { name: "notes", label: "notas", prompt: "¿Alguna condición o contexto? Decí siguiente si no.", kind: "text" },
      ],
    },
  });

  const incomeNeedsRelations = form.direction === "income" && !structuralLocked;
  const relationsBlocked = incomeNeedsRelations && (loadingRelations || Boolean(relationsError));
  const saveDisabled = saving
    || (form.amountMode === "fixed" && !(Number(form.fixedAmount) > 0))
    || (incomeNeedsRelations && (!form.clientId || !form.serviceId || form.serviceId === "__quick__" && !form.quickServiceName.trim()))
    || relationsBlocked;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: .8 }}>
        <Typography component="div" sx={{ fontSize: "1.15rem", fontWeight: 1000, letterSpacing: "-.02em" }}>{isEditing ? "Editar acuerdo recurrente" : "Nuevo acuerdo recurrente"}</Typography>
        <Typography sx={{ mt: .35, color: "text.secondary", fontWeight: 700, fontSize: ".86rem" }}>
          La regla proyecta próximas ocurrencias. El dinero real sólo existe cuando registrás o vinculás un movimiento.
        </Typography>
        <OwnerVoiceFormControls />
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={1.35}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {relationsError ? <Alert severity={form.direction === "income" ? "warning" : "info"}>{relationsError}</Alert> : null}
          {structuralLocked ? <Alert severity="info">Este acuerdo ya tiene registros reales. Para preservar el historial, cliente, servicio, moneda, tipo, frecuencia e inicio quedan bloqueados. Podés ajustar monto futuro, día de cobro mensual, cuenta, fin y completar un proyecto/acuerdo que todavía no estaba vinculado.</Alert> : null}
          {isEditing && plan?.direction === "income" && (!idOf(plan.clientId) || !idOf(plan.serviceId)) ? <Alert severity="warning">Este acuerdo histórico está incompleto: falta relacionar {idOf(plan.clientId) ? "servicio" : "cliente o servicio"}. Sigue operativo; para cambiar relaciones con historial, terminá este acuerdo y creá uno nuevo.</Alert> : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <FormControl fullWidth disabled={structuralLocked}><InputLabel>Tipo</InputLabel><Select inputProps={voiceFieldProps("direction")} label="Tipo" value={form.direction} onChange={(event) => setForm({ ...form, direction: event.target.value, ...(event.target.value === "income" ? { area: "Freelancer", category: "Servicio recurrente" } : {}) })}><MenuItem value="expense">Pago / gasto recurrente</MenuItem><MenuItem value="income">Cobro / servicio recurrente</MenuItem></Select></FormControl>
            <FormControl fullWidth><InputLabel>Monto</InputLabel><Select inputProps={voiceFieldProps("amountMode")} label="Monto" value={form.amountMode} onChange={(event) => setForm({ ...form, amountMode: event.target.value })}><MenuItem value="fixed">Fijo</MenuItem><MenuItem value="variable">Variable</MenuItem></Select></FormControl>
            <FormControl sx={{ minWidth: 140 }} disabled={structuralLocked}><InputLabel>Moneda</InputLabel><Select inputProps={voiceFieldProps("currency")} label="Moneda" value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value, defaultAccountId: "" })}><MenuItem value="ARS">ARS</MenuItem><MenuItem value="USD">USD</MenuItem></Select></FormControl>
          </Stack>

          {form.direction === "income" ? (
            <Box sx={{ p: 1.35, borderRadius: 2.3, bgcolor: "rgba(243,90,58,.045)", border: "1px solid rgba(243,90,58,.12)" }}>
              <Typography sx={{ fontWeight: 950, mb: 1 }}>Relación comercial</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                <FormControl fullWidth disabled={loadingRelations || structuralLocked}><InputLabel>Persona / cliente</InputLabel><Select inputProps={voiceFieldProps("clientId")} label="Persona / cliente" value={form.clientId} onChange={(event) => setForm({ ...form, clientId: event.target.value })}>{clients.map((client) => <MenuItem key={idOf(client)} value={idOf(client)}>{client.name}{client.company ? ` · ${client.company}` : ""}</MenuItem>)}</Select></FormControl>
                <FormControl fullWidth disabled={loadingRelations || structuralLocked}><InputLabel>Servicio</InputLabel><Select inputProps={voiceFieldProps("serviceId")} label="Servicio" value={form.serviceId} onChange={(event) => changeService(event.target.value)}><MenuItem value="__quick__">+ Crear servicio</MenuItem>{services.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name} · {row.currency}</MenuItem>)}</Select></FormControl>
              </Stack>
              {form.serviceId === "__quick__" && !structuralLocked ? <TextField inputProps={voiceFieldProps("quickServiceName")} sx={{ mt: 1.2 }} fullWidth label="Nombre del servicio" placeholder="Ej.: WebProfit" value={form.quickServiceName} onChange={(event) => setForm({ ...form, quickServiceName: event.target.value })} /> : null}
            </Box>
          ) : null}

          <Box sx={{ p: 1.25, borderRadius: 2.3, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontWeight: 950, mb: .25 }}>Proyecto / contrato</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".76rem", fontWeight: 700, mb: 1 }}>Si esta mensualidad sostiene un proyecto WebProfit, vinculala una vez. Después aparece en su ficha y en tu planificación futura.</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              <FormControl fullWidth disabled={projectRelationLocked}>
                <InputLabel>Proyecto</InputLabel>
                <Select inputProps={voiceFieldProps("initiativeId")} label="Proyecto" value={form.initiativeId} onChange={(event) => changeInitiative(event.target.value)}>
                  <MenuItem value="">Sin proyecto</MenuItem>
                  {initiatives.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name}{row.client?.name ? ` · ${row.client.name}` : ""}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={!form.initiativeId || loadingProjectTerms || termRelationLocked}>
                <InputLabel>Acuerdo / bloque</InputLabel>
                <Select inputProps={voiceFieldProps("projectTermId")} label="Acuerdo / bloque" value={form.projectTermId} onChange={(event) => setForm({ ...form, projectTermId: event.target.value })}>
                  <MenuItem value="">Sólo vincular al proyecto</MenuItem>
                  {projectTerms.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.title} · {row.billingMode === "monthly" ? "mensual" : "contrato/bloque"}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            {selectedInitiative?.webProfitProject ? <Typography sx={{ mt: .65, color: "text.secondary", fontSize: ".72rem", fontWeight: 800 }}>WebProfit vinculado: {selectedInitiative.webProfitProject.name}</Typography> : null}
          </Box>

          <TextField inputProps={voiceFieldProps("name")} fullWidth label="Cómo reconocer este acuerdo" placeholder={form.direction === "income" ? "Ej.: WebProfit · Cliente A" : "Ej.: Alquiler, Internet, licencia"} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          {form.amountMode === "fixed" ? (
            <TextField inputProps={voiceFieldProps("fixedAmount")} fullWidth type="number" label={`Monto por ocurrencia · ${form.currency}`} value={form.fixedAmount} onChange={(event) => setForm({ ...form, fixedAmount: event.target.value })} />
          ) : (
            <Box sx={{ p: 1.4, borderRadius: 2.3, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.035)", border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.055)" }}>
              <TextField inputProps={voiceFieldProps("estimatedAmount")} fullWidth type="number" label={`Estimación inicial opcional · ${form.currency}`} value={form.estimatedAmount} onChange={(event) => setForm({ ...form, estimatedAmount: event.target.value })} />
              <Typography sx={{ mt: .7, color: "text.secondary", fontWeight: 700, fontSize: ".78rem" }}>Con hechos reales, la proyección usa el promedio de hasta los últimos 6 períodos saldados.</Typography>
            </Box>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <FormControl fullWidth disabled={structuralLocked}><InputLabel>Frecuencia</InputLabel><Select inputProps={voiceFieldProps("frequency")} label="Frecuencia" value={form.frequency} onChange={(event) => setForm({ ...form, frequency: event.target.value })}>{OWNER_RECURRING_FREQUENCIES.map((row) => <MenuItem key={row.value} value={row.value}>{row.label}</MenuItem>)}</Select></FormControl>
            {form.frequency === "monthly" ? <TextField fullWidth type="number" label="Día de cobro" inputProps={{ ...voiceFieldProps("dueDay"), min: 1, max: 31 }} value={form.dueDay} disabled={projectFinancingPlan} onChange={(event) => setForm({ ...form, dueDay: event.target.value })} helperText={projectFinancingPlan ? "Esta recurrencia viene del contrato financiado. Cambiá la primera cuota desde Proyecto → Cobros → Contrato/cuotas." : structuralLocked ? "Podés mover los próximos cobros sin alterar el historial." : "1–31"} /> : null}
            <TextField inputProps={voiceFieldProps("startDate")} fullWidth type="date" label="Inicio del acuerdo" InputLabelProps={{ shrink: true }} value={form.startDate} disabled={structuralLocked} onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
            <TextField inputProps={voiceFieldProps("firstDueDate")} fullWidth type="date" label="Primer vencimiento" InputLabelProps={{ shrink: true }} value={form.firstDueDate} disabled={structuralLocked} onChange={(event) => setForm({ ...form, firstDueDate: event.target.value })} />
            <TextField inputProps={voiceFieldProps("endDate")} fullWidth type="date" label="Termina (opcional)" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <FormControl fullWidth><InputLabel>Cuenta habitual</InputLabel><Select inputProps={voiceFieldProps("defaultAccountId")} label="Cuenta habitual" value={form.defaultAccountId} onChange={(event) => setForm({ ...form, defaultAccountId: event.target.value })}><MenuItem value="">Elegir al registrar</MenuItem>{eligibleAccounts.map((account) => <MenuItem key={idOf(account)} value={idOf(account)}>{account.name} · {account.currency}</MenuItem>)}</Select></FormControl>
            <TextField fullWidth label="Área" value={form.area} onChange={(event) => setForm({ ...form, area: event.target.value })} />
            <TextField fullWidth label="Categoría" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} />
          </Stack>
          <TextField inputProps={voiceFieldProps("notes")} fullWidth multiline minRows={2} label="Notas" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.7 }}>
        <Button onClick={onClose} disabled={saving} sx={compactActionSx}>Cancelar</Button>
        <Button variant="contained" onClick={() => voice?.submitCurrentForm ? voice.submitCurrentForm() : submit()} disabled={saveDisabled} sx={{ ...compactActionSx, px: 2.3 }}>
          {saving ? "Guardando…" : isEditing ? "Guardar cambios" : "Crear recurrente"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
